import * as Model from './index';
import { AxiosResponse } from 'axios';
import { cloneDeep } from 'lodash';
import axios from 'services/axios';

const SENCROP_USERS_PARTNERS_URL = import.meta.env.VITE_APP_SENCROP_USERS_PARTNERS_API_URL;
const SENCROP_PARTNER_DEVICES_URL = import.meta.env.VITE_APP_SENCROP_USERS_PARTNER_DEVICES_API_URL;
const ARABLE_DEVICES_API_URL = import.meta.env.VITE_APP_ARABLE_DEVICES_API_URL;
const TRAPVIEW_DEVICES_API_URL = import.meta.env.VITE_APP_TRAP_VIEW_DEVICES_API_URL;
const GROGURU_SITES_API_URL = import.meta.env.VITE_APP_GROGURU_SITES_API_URL;
const PESSL_STATIONS_API_URL = import.meta.env.VITE_APP_PESSL_STATIONS_API_URL;
const VALENCO_DEVICES_API_URL = import.meta.env.VITE_APP_VALENCO_DEVICES_URL;

/**
 * constructs partner per the given partner id
 * @param partnerId integration's partner name
 * @returns breadcrumb area
 */
export const getConfigurationBreadcrumb = (partnerId: string) => {
  return [
    { name: 'Integrations', path: `/integrations` },
    { name: partnerId, path: `integrations/${partnerId}` },
    { name: 'Configure Integration', path: `/integrations/${partnerId}/configure-integration` },
  ];
};

/**
 * retrieve Sencrop Partners (Organizations)
 */
export const getSencropUsersPartners = async (accessToken: string) => {
  try {
    const response = await axios.request({
      method: 'GET',
      url: SENCROP_USERS_PARTNERS_URL,
      headers: { authorization: `Bearer ${accessToken}` },
    });
    if (!response.data) {
      return Promise.reject('Failed to retrieve partners');
    }
    return response.data;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

const getSencropRestOfAllDevices = async (
  devicesRequests: Promise<Model.SencropDevicesResponse>[],
  _devices: { [key: string]: any },
) => {
  let devices = cloneDeep(_devices);
  let allResponses = await Promise.allSettled(devicesRequests);
  if (allResponses?.length > 0) {
    allResponses.forEach((res: any) => {
      if (res.status === 'fulfilled' && res.value) {
        devices = { ...devices, ...res.value.data.devices };
      } else if (res.status === 'rejected') {
        console.error(res.reason);
      }
    });
  }
  return devices;
};

/**
 * Sencrop partner-device api returns only 100 items per call
 * hence if the total value is greater than 100, we will iterate to retrieve all devices
 * @param accessToken
 * @param partnerId
 * @returns list off all devices related to the given partner
 */
export const getSencropPartnerDevices = async (accessToken: string, partnerId: number) => {
  try {
    let devices: { [key: string]: any } = {};
    let devicesRequests: any[] = [];

    let response: AxiosResponse<Model.SencropDevicesResponse> = await axios.request({
      method: 'GET',
      url: `${SENCROP_PARTNER_DEVICES_URL?.replace(
        '{{partner_id}}',
        partnerId?.toString(),
      )}?limit=100&start=0`,
      headers: { authorization: `Bearer ${accessToken}` },
    });
    if (response?.data?.items) {
      devices = response.data.devices;
      const total = response.data.total;
      if (response.data.items.length < total) {
        let limitsList = [];
        let startIndex = response.data.items.length;
        let limit = 100;
        while (startIndex < total) {
          limitsList.push({
            start: startIndex,
            limit: limit,
          });
          startIndex += limit;
        }

        limitsList.forEach((_limitRange) => {
          devicesRequests.push(
            axios.request({
              method: 'GET',
              url: `${SENCROP_PARTNER_DEVICES_URL?.replace(
                '{{partner_id}}',
                partnerId?.toString(),
              )}?limit=${_limitRange.limit}&start=${_limitRange.start}`,
              headers: { authorization: `Bearer ${accessToken}` },
            }),
          );
        });
      }
    }
    response.data.devices = await getSencropRestOfAllDevices(devicesRequests, devices);
    return response.data;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

const getArableDevices = async (user_id: string, limit: number) => {
  return axios.request<Model.ArableDevicesResponse>({
    method: 'GET',
    url: ARABLE_DEVICES_API_URL,
    params: {
      user_id: user_id.split('_')[0],
      org_id: user_id.split('_')[1],
      limit: limit,
    },
  });
};

/**
 * retrieve Arable devices by the logged in user
 * @param accessToken
 * @param page
 * @param limit
 * @returns
 */
export const getAllArableDevices = async (user_id: string) => {
  try {
    const limit = 2000;
    let response: AxiosResponse<Model.ArableDevicesResponse> = await getArableDevices(
      user_id,
      limit,
    );
    if (response.data.total > limit) {
      response = await getArableDevices(user_id, response.data.total);
    }
    return response.data;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getTrapViewDevices = async (
  idSubject: number | string,
  application_id: string,
  application_secret: string,
) => {
  try {
    if (idSubject) {
      const authToken = btoa(`${application_id}:${application_secret}`);
      const response = await axios.request({
        method: 'GET',
        url: `${TRAPVIEW_DEVICES_API_URL}/${idSubject}`,
        headers: { authorization: `Basic ${authToken}` },
      });
      if (response.data) {
        let devices: any[] = response.data;
        return devices.map((_deviceItem: any) => {
          return { ..._deviceItem.device };
        });
      } else {
        return Promise.reject('Failed to retrieve devices');
      }
    }
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getGroGuruSites = async (token: { id: string; sessionToken: string }) => {
  try {
    if (token) {
      const response: AxiosResponse<Model.GroGruSitesResponse> = await axios.request({
        method: 'GET',
        url: `${GROGURU_SITES_API_URL}/${token.id}`,
        headers: { authorization: token.sessionToken },
      });
      if (response.data) {
        let organizations = response.data.data?.children;
        let sites: any[] = [];
        organizations?.forEach((item) => {
          sites.push(
            ...item.sites.map((_site: any) => {
              _site.farm = item.name;
              return _site;
            }),
          );
        });
        return sites.map((_site: any) => {
          _site.idOrg = _site.id;
          _site.id = _site.siteId;
          return _site;
        });
      }
    }
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
  return [];
};

export const getPesslStations = async (accessToken: string) => {
  try {
    const response: AxiosResponse<Model.PesslStation[]> = await axios.request({
      method: 'GET',
      url: PESSL_STATIONS_API_URL,
      headers: { authorization: `Bearer ${accessToken}` },
    });
    if (!response.data) {
      return Promise.reject('Failed to retrieve stations');
    }
    return response.data;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getVerisUsers = async (user_org_id: string) => {
  try {
    const response: AxiosResponse<Model.VerisUsersResponse[]> = await axios.request({
      method: 'GET',
      url: `${import.meta.env.VITE_APP_API_URL}/valet/v2/veris-growers`,
      params: {
        user_id: user_org_id.split('_')[0],
        org_id: user_org_id.split('_')[1],
      },
    });
    if (!response.data) {
      return Promise.reject('Failed to retrieve users');
    }
    return response.data;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getVerisFarms = async (user_org_id: string, growers: string[]) => {
  try {
    if (growers?.length) {
      let farmsRequests: Promise<any>[] = [];
      growers.forEach((_grower) => {
        farmsRequests.push(
          axios.request<Model.VerisFarmsResponse>({
            method: 'GET',
            url: `${import.meta.env.VITE_APP_API_URL}/valet/v2/veris-farms`,
            params: {
              grower: _grower,
              user_id: user_org_id.split('_')[0],
              org_id: user_org_id.split('_')[1],
            },
          }),
        );
      });
      const allResponses = await Promise.allSettled(farmsRequests);
      if (allResponses?.length > 0) {
        let farms: Model.VerisFarm[] = [];
        allResponses.forEach((res: any) => {
          if (res.status === 'fulfilled' && res.value?.data) {
            farms.push(
              ...res.value.data.Farms.map((_farm: string) => {
                return {
                  id: _farm,
                  grower: res.value.config?.params?.grower,
                };
              }),
            );
          } else if (res.status === 'rejected') {
            console.error(res.reason);
          }
        });
        return farms;
      } else {
        return Promise.reject('No farms retrieved');
      }
    } else {
      return Promise.reject('No users selected');
    }
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const generateVerisSettings = (
  filter: Model.VerisFilterSetting[],
  farms: Model.VerisFarm[],
  selectedItems: string[],
) => {
  let payload: Model.VerisFarmAuthAccount[] = [];
  if (farms?.length && filter) {
    const growerFarmsMap = new Map<string, string[]>();
    const growers = Array.from(new Set(farms.map((_item: Model.VerisFarm) => _item.grower)));
    if (selectedItems.length) {
      const filteredFarms = farms.filter((_farm: Model.VerisFarm) => {
        return selectedItems.includes(_farm.id) && growers.includes(_farm.grower);
      });
      filteredFarms.forEach((_farm: Model.VerisFarm) => {
        if (growerFarmsMap.get(_farm.grower)) {
          growerFarmsMap.set(_farm.grower, [...growerFarmsMap.get(_farm.grower)!, _farm.id]);
        } else {
          growerFarmsMap.set(_farm.grower, [_farm.id]);
        }
      });
      for (let [_grower, _farms] of growerFarmsMap) {
        payload.push({
          grower: _grower,
          farms: _farms,
        });
      }
    } else {
      payload = growers.map((_item: string) => {
        return {
          grower: _item,
        };
      });
    }
  }
  return payload;
};

/**
 * Get Valenco's devices by page
 *
 * @param accessToken
 * @param page
 * @returns
 */
const getValencoDevices = async (accessToken: string, page: number = 0) => {
  return axios.request<Model.ValencoDevicesResponse>({
    method: 'GET',
    url: VALENCO_DEVICES_API_URL,
    params: {
      page: page,
      pageSize: 10000,
      sortProperty: 'createdTime',
      sortOrder: 'ASC',
    },
    headers: { authorization: `Bearer ${accessToken}` },
  });
};

/**
 * Retrieve all Valenco's devices by the logged in user
 *
 * @param accessToken
 * @returns
 */
export const getAllValencoDevices = async (accessToken: string) => {
  try {
    let devices: Model.ValencoDevice[] = [];
    let response: AxiosResponse<Model.ValencoDevicesResponse> = await getValencoDevices(
      accessToken,
    );
    devices = response.data?.data ?? [];
    for (let _pageNumber = 1; _pageNumber < response.data.totalPages; _pageNumber++) {
      response = await getValencoDevices(accessToken, _pageNumber);
      if (response.data?.data?.length) {
        devices.push(...(response.data?.data ?? []));
      }
    }
    return devices;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};
